import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useLocation, matchPath } from "react-router-dom";
import { useSelector } from "react-redux";
import RoutesFile from "../../RouteList/RoutesFile";

const SurveyContext = createContext(null);

const INITIAL_ANSWERS = {
  recommendScore: null, // 0-10 (step 1)
  serviceRating: null, // emoji key (step 2)
  serviceFeedback: "", // step 2 text feedback
  platformRating: null, // emoji key (step 3)
  platformFeedback: "", // step 3 text feedback
  suggestions: "", // step 4
};

function isAllowedSurveyRoute(pathname) {
  if (!pathname || pathname === "/") return false;
  return RoutesFile.some((route) => matchPath(route.path, pathname));
}

export function SurveyProvider({
  children,
  autoOpenAfterMs = null,
  autoOpenFlag = true,
}) {
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const autoOpenTimeoutRef = useRef(null);

  const showPopupFromApi = useSelector((state) => state.Survey?.showPopup);
  const isAllowedRoute = isAllowedSurveyRoute(pathname);

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [answers, setAnswersState] = useState(INITIAL_ANSWERS);

  const setAnswer = useCallback((key, value) => {
    setAnswersState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const openSurvey = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeSurvey = useCallback(() => {
    setIsOpen(false);
    // Keep step and answers so "close without losing data" is satisfied
  }, []);

  const resetSurvey = useCallback(() => {
    setStep(1);
    setAnswersState(INITIAL_ANSWERS);
    setIsOpen(false);
  }, []);

  const nextStep = useCallback(() => {
    setStep((s) => Math.min(s + 1, 4));
  }, []);

  const previousStep = useCallback(() => {
    setStep((s) => Math.max(s - 1, 1));
  }, []);

  const canGoNextStep1 =
    answers.recommendScore !== null && answers.recommendScore !== "";
  const canGoNextStep2 = answers.serviceRating != null;
  const canGoNextStep3 = answers.platformRating != null;
  const canGoNextStep4 = (answers.suggestions ?? "").trim() !== "";
  const canGoNext =
    step === 1
      ? canGoNextStep1
      : step === 2
        ? canGoNextStep2
        : step === 3
          ? canGoNextStep3
          : canGoNextStep4;

  const submitSurvey = useCallback(() => {
    const payload = {
      recommendScore: answers.recommendScore,
      serviceRating: answers.serviceRating,
      platformRating: answers.platformRating,
      suggestions: answers.suggestions?.trim() || "",
    };
    // Mock API call
    console.log("[Survey] Submit payload:", payload);
    resetSurvey();
  }, [answers, resetSurvey]);

  // Close survey when user navigates to home or 404 (disallowed route)
  useEffect(() => {
    if (!isAllowedRoute) closeSurvey();
  }, [isAllowedRoute, closeSurvey]);

  // Auto-open when API says showPopup; close again once it flips false
  useEffect(() => {
    if (!autoOpenFlag || !isAllowedRoute) return;
    if (showPopupFromApi === true) {
      if (!isAllowedSurveyRoute(pathnameRef.current)) return;
      openSurvey();
      return;
    }
    if (showPopupFromApi === false && isOpen) {
      resetSurvey();
    }
  }, [
    autoOpenFlag,
    openSurvey,
    isAllowedRoute,
    isOpen,
    resetSurvey,
    showPopupFromApi,
  ]);

  const value = {
    isOpen,
    openSurvey,
    closeSurvey,
    step,
    setStep,
    answers,
    setAnswer,
    resetSurvey,
    nextStep,
    previousStep,
    canGoNext,
    submitSurvey,
  };

  return (
    <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>
  );
}

export function useSurvey() {
  const ctx = useContext(SurveyContext);
  if (!ctx) {
    throw new Error("useSurvey must be used within a SurveyProvider");
  }
  return ctx;
}
